import Vue from "vue";
import CXMApiClient from "../../CXMApiClient";
import { exists, getResponseErrorMessages } from "@/lib/utilities";
import { HelperFunctions } from "@/lib/utilities/HelperFunctions.js";

const state = {
  DeskItStatus: [],
  DeskItStatusLoaded: false,
  ContactLeadReassignModal: {
    ShowModal: false,
    PlatformId: null,
    ContactId: null,
    ContactName: null,
    Lead: null,
    RequestOrigin: null
  },
  ChangeLeadStatusModal: {
    showModal: false,
    data: null
  },
  ChangeLeadTypeSourceModal: {
    ShowModal: false,
    Lead: null
  }
};

const getters = {
  ChangeLeadStatusModalInfo: state => state.ChangeLeadStatusModal,
  ChangeLeadTypeSourceModalInfo: state => state.ChangeLeadTypeSourceModal,
  DeskItStatusLoaded: state => state.DeskItStatusLoaded,
  getDeskItStatus: state => accountPlatformId => {
    if (!state.DeskItStatus || state.DeskItStatus.length == 0) return null;
    return state.DeskItStatus.find(
      status => status.accountPlatformId == accountPlatformId
    );
  },
  hasDeskItEnabled: (state, getters) => accountPlatformId => {
    const enablement = getters.getDeskItStatus(accountPlatformId);
    return (
      enablement && enablement.isDeskingEnabled && enablement.deskingProvider
    );
  }
};

const actions = {
  logCallForEntity(context, requestPayload) {
    const scope = HelperFunctions.getCurrentCustomerScope();
    return new Promise((resolve, reject) => {
      let cxmApiClient = new CXMApiClient();
      cxmApiClient
        .ProcessRequest(
          "POST",
          `/crm/contacts/${requestPayload.contactId}/actions/log_phone_call`,
          requestPayload,
          scope
        )
        .then(resp => {
          if (resp.data && resp.data.successful) resolve(resp);
          else reject(resp);
        })
        .catch(resp => {
          reject(resp);
        });
    });
  },
  logNoteForEntity(context, requestPayload) {
    const scope = HelperFunctions.getCurrentCustomerScope();
    return new Promise((resolve, reject) => {
      let cxmApiClient = new CXMApiClient();
      cxmApiClient
        .ProcessRequest(
          "POST",
          `/crm/contacts/${requestPayload.contactId}/actions/log_note`,
          requestPayload,
          scope
        )
        .then(resp => {
          if (resp.data && resp.data.successful) resolve(resp);
          else reject(resp);
        })
        .catch(resp => {
          reject(resp);
        });
    });
  },
  async sendEmailForEntity(context, requestPayload) {
    const response = await Vue.http
      .post("/pando/pandox/sendemail", requestPayload)
      .catch(err => err.response);
    if (response.data && response.data.Success)
      return {
        Success: true
      };
    else
      return {
        Success: false,
        RestrictedEmailAddress:
          response.data &&
          response.data.Data &&
          response.data.Data.ErrorMessage &&
          response.data.Data.ErrorMessage.includes("RestrictedEmailAddress")
      };
  },
  async uploadTinyMCEImages(context, request) {
    let response = await Vue.http.post(
      `/pando/pandox/create_email_template_attachment/${request.AccountID}/${request.AccountPlatformID}`,
      request.FileContent
    );

    if (
      response &&
      response.data &&
      response.data.Data &&
      response.data.Data[0].FilePath
    ) {
      return response.data.Data[0].FilePath;
    }
    return null;
  },
  getEmailTemplatesForPlatform(context, accountPlatformId) {
    return new Promise((resolve, reject) => {
      let requestPayload = {
        CategoryIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
        ChannelIds: [1],
        Statuses: [true],
        TemplateTypeId: 1,
        AccountPlatformIds: [accountPlatformId]
      };

      Vue.http
        .post("/templatemanager/getDealerTemplates", requestPayload)
        .then(resp => {
          if (resp.data && resp.data.Success) resolve(resp.data.Data || []);
          else reject(resp);
        })
        .catch(resp => {
          reject(resp);
        });
    });
  },
  getSMSTemplatesForPlatform(context, accountPlatformId) {
    return new Promise((resolve, reject) => {
      let requestPayload = {
        CategoryIds: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14],
        ChannelIds: [3],
        Statuses: [true],
        TemplateTypeId: 1,
        AccountPlatformIds: [accountPlatformId]
      };

      Vue.http
        .post("/templatemanager/getDealerTemplates", requestPayload)
        .then(resp => {
          if (resp.data && resp.data.Success) resolve(resp.data.Data || []);
          else reject(resp);
        })
        .catch(resp => {
          reject(resp);
        });
    });
  },
  async getMergedEmailTemplateForEntity(context, requestPayload) {
    const response = await Vue.http
      .post("/pando/pandox/tokenreplaceemailtemplate", requestPayload)
      .catch(err => err.response);

    return response.data && response.data.Success ? response.data.Data : null;
  },
  getDeskItStatus(context) {
    const cxmApiClient = new CXMApiClient();
    cxmApiClient
      .ProcessRequest("POST", "/crm/contacts/actions/is_desking_enabled")
      .then(resp => {
        if (resp.data && resp.data.successful)
          context.commit("gotDeskItStatus", resp.data.data);
      })
      .catch(resp => {
        console.log("Error fetching DeskIt status: ", resp);
      });
  },
  async pushToDeskit(context, requestPayload) {
    const cxmApiClient = new CXMApiClient();

    const response = await cxmApiClient
      .ProcessRequest(
        "POST",
        `/crm/contacts/${requestPayload.contactId}/actions/send_to_desking`,
        requestPayload
      )
      .catch(err => err.response);

    return response.data && response.data.successful
      ? response.data.data
      : null;
  },
  pushToCrm(context, requestPayload) {
    return new Promise((resolve, reject) => {
      const cxmApiClient = new CXMApiClient();
      cxmApiClient
        .ProcessRequest("POST", "api/Dealsheet/push_to_crm", requestPayload)
        .then(resp => {
          if (!resp || !resp.data || !resp.data.successful || !resp.data.data)
            reject({
              success: false,
              message:
                "There was an error processing your request. Please try again later."
            });
          else
            resolve({
              success: true,
              message: "Successfully pushed to crm.",
              activityCodeResult: resp.data.data.result,
              resultData: resp.data.data.resultData
            });
        })
        .catch(err => {
          let message =
            getResponseErrorMessages(err) ||
            "There was an error sending the information to Crm. Please try again later.";
          if (Array.isArray(message)) message = message.join(" ");
          reject({
            success: false,
            message
          });
        });
    });
  },
  getUserPermissionsForContact: function(context, data) {
    return new Promise((resolve, reject) => {
      let client = new CXMApiClient();
      client
        .ProcessRequest(
          "get",
          "/api/contacts/" + data.contactId + "/user_permissions/" + data.userId
        )
        .then(response => {
          if (response && response.data.successful) {
            resolve(response.data.data);
            if (response.data.data.canView) {
              context.dispatch("setUserContactPermissions", response.data.data);
            }
          } else reject(response);
        })
        .catch(err => {
          reject(err);
        });
    });
  },
  updateChangeLeadStatusModal(context, data) {
    context.commit("handleChangeLeadStatusModal", data);
  },
  openChangeLeadTypeSourceModal(context, data) {
    context.commit("handleChangeLeadTypeSourceModal", {
      Lead: data.Lead,
      ShowModal: true
    });
  },
  closeChangeLeadTypeSourceModal(context) {
    context.commit("handleChangeLeadTypeSourceModal", {
      Lead: null,
      ShowModal: false
    });
  },
  openContactLeadReassignModal(context, data) {
    context.commit("openContactLeadReassignModal", data);
  },
  closeContactLeadReassignModal(context) {
    context.commit("closeContactLeadReassignModal");
  }
};

const mutations = {
  gotDeskItStatus(state, data) {
    state.DeskItStatusLoaded = true;
    state.DeskItStatus = data || [];
  },
  handleChangeLeadStatusModal(state, data) {
    state.ChangeLeadStatusModal = data;
  },
  handleChangeLeadTypeSourceModal(state, data) {
    state.ChangeLeadTypeSourceModal = data;
  },
  openContactLeadReassignModal(state, data) {
    state.ContactLeadReassignModal.PlatformId = data.PlatformId;
    state.ContactLeadReassignModal.ContactId = data.ContactId;
    state.ContactLeadReassignModal.ContactName = data.ContactName;
    state.ContactLeadReassignModal.Lead = data.Lead;
    state.ContactLeadReassignModal.ShowModal = true;
    state.ContactLeadReassignModal.RequestOrigin = data.RequestOrigin;
  },
  closeContactLeadReassignModal(state) {
    state.ContactLeadReassignModal.ShowModal = false;
    state.ContactLeadReassignModal.PlatformId = null;
    state.ContactLeadReassignModal.ContactId = null;
    state.ContactLeadReassignModal.ContactName = null;
    state.ContactLeadReassignModal.Lead = null;
    state.ContactLeadReassignModal.RequestOrigin = null;
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/customerdashboard/ContactActions.js